import { ReactNode, useState, Children, isValidElement, cloneElement, Fragment } from 'react';
import { SubMenuProvider, useSubMenu } from './SubMenuContext';
import TopBar from './TopBar';
import { AnimatePresence, motion } from 'framer-motion';
import Content from './Content';
import ContentApp from './ContentApp';
import Separator from './ui/Separator';
import ItemMenu from './ui/ItemMenu';
import { Copy } from 'lucide-react';
import { useCopyToClipboard, useWindowDimensions } from './lib/hook/copyClipBoard';
import { Toaster } from 'sonner';
import IntegracaoBtn from './ui/SideBarBtns/IntegracaoBtn';
import { TooltipProvider } from './ui/Tooltip/Tooltip';
import { FAQEditorProvider } from './features/FaqEditor/Context/FaqEditorContext';
import FaqContent from './features/FaqEditor/FaqContent';
import BuilderContent from './features/component-builder/BuilderContent';
import { TranslationProvider, useTranslation } from './lib/i18n/TranslationContext';
import esMX from './locales/es-MX.json';
import ptBr from './locales/pt-BR.json';
import enUS from './locales/en-US.json';
import RoleSwitcher from './ui/RoleSwitcher';
import Chat from './features/Chat/Chat';
import { ChatProvider } from './context/ChatContext';
import ButtonDash from './ui/ButtonDash';
import SideBarContentComponent from './ui/SideBarContentComponent';
import SidebarMenu from './ui/Sidebar/SidebarMenu';
import { AccordionProvider } from './context/ComponentesSlots';
import ThirdMenu from './ui/ThirdMenu';
import { UserProvider, useUser } from './UserContext';
import PerfilWrapper from './PerfilWrapper';
import CurrentStoreWrapper from './CurrentStoreWrapper';
import NavigationAppsQuickAcessMenu from './ui/NavigationAppsQuickAcessMenu/NavigationAppsQuickAcessMenu';
import { NavigationBlockerAuto } from './ui/NavigationBlocker';
import { TourAuto } from './ui/Tour/Tour';

type LanguageType = 'pt-br' | 'es-mx' | 'en-us';

type HeadersType = {
    [key: string]: string;
};
interface SideBarProps {
    children: ReactNode;
    app: string;
    classeAtiva?: string;
    isOpen?: boolean;
    appEndPoint?: string;
    headers?: HeadersType;
    chave_empreender?: string;
    onKeyCopy?: (key: string) => void;
    expiredAccountPopUpEnabled?: () => boolean;
    idioma: LanguageType;
    sideWidth?: number;
    hideApps?: boolean;
    hideDash?: boolean;
    iframe?: boolean;
    navigate?: (href: string) => void;
    /** Chave da Google Fonts API, consumida pelo FontPicker. */
    googleFontsKey?: string;
}

interface ItemMenuProps {
    setSidebarOpen: (toggle: boolean) => void;
    classeAtiva?: string;
}

interface SeparatorProps {
    setSidebarOpen: (toggle: boolean) => void;
}

export interface EplusApps {
    [key: string]: {
        integrations?: Integration;
    };
}

declare global {
    interface Window {
        configsAppsPlus: EplusApps;
    }
}

interface Integration {
    [key: string]: {
        show: boolean;
        img: string;
    };
}

export interface CurrentStoreProps {
    chave_empreender: string;
    dias_trial_total: number;
    vencimento_trial: number;
    dias_restantes_trial: number;
    exibir_box_review: boolean;
    plataforma_review: string;
    app_vencimento: number;
    link_loja: string;
    nome_plano: string;
    link_abrir_modal_pagamento?: string;
    iframe?: boolean;
}

const DesenvolvidoPor = () => {
    const { t } = useTranslation();
    return (
        <>
            {t('Desenvolvido por')}{' '}
            <a className='text-gray-900 font-semibold border-b border-b-[#8346F9]' href={t('https://empreender.com.br')} target='_blank'>
                Empreender
            </a>
        </>
    );
};

const SideBar = ({ children, app, classeAtiva, isOpen = true, appEndPoint, headers, iframe = false, idioma = 'pt-br', onKeyCopy = () => {}, expiredAccountPopUpEnabled = () => true, sideWidth = 280, hideApps = false, hideDash = false, navigate, googleFontsKey }: SideBarProps) => {
    const { width } = useWindowDimensions();

    const messages: Record<LanguageType, Record<string, string>> = {
        'pt-br': ptBr,
        'es-mx': esMX,
        'en-us': enUS,
    };

    const lingua: LanguageType = typeof messages[idioma] !== 'undefined' ? idioma : 'pt-br';

    const [isSidebarOpen, setSidebarOpen] = useState<boolean>(isOpen ?? true);

    const sidebarElements = Children.map(children, (child) => {
        if (!isValidElement(child)) return null;

        if (child.type === Fragment) {
            return child;
        }

        if (child.type === ItemMenu || child.type === Separator) {
            return cloneElement(child as React.ReactElement<ItemMenuProps | SeparatorProps>, {
                setSidebarOpen,
                classeAtiva,
            });
        }

        return null;
    });

    const contents = Children.map(children, (child) => (isValidElement(child) && child.type === Content ? child : null));

    contents?.push(<FaqContent key='faq-editor' />);
    contents?.push(<BuilderContent key='component-builder' />);

    const contentApp = Children.map(children, (child) => (isValidElement(child) && child.type === ContentApp ? child : null));

    const IntegracaoBtnChildren = Children.map(children, (child) => {
        if (isValidElement(child) && child.type === IntegracaoBtn) {
            return cloneElement(child as React.ReactElement<ItemMenuProps | SeparatorProps>, {
                setSidebarOpen,
                classeAtiva,
            });
        }
        return null;
    });

    const RoleSwitcherContent = Children.map(children, (child) => {
        if (isValidElement(child) && child.type === RoleSwitcher) {
            return cloneElement(child as React.ReactElement);
        }
        return null;
    });

    const showQuickAppsMenu = !hideApps;

    return (
        <UserProvider appEndPoint={appEndPoint} headers={headers} hideApps={hideApps} iframe={iframe}>
            <TranslationProvider messages={messages[lingua]}>
                <TooltipProvider disableHoverableContent>
                    <SubMenuProvider app={app} sideWidth={sideWidth} isSidebarOpen={isSidebarOpen} setSideBarOpen={setSidebarOpen} appEndPoint={appEndPoint} headers={headers} googleFontsKey={googleFontsKey}>
                        <ChatProvider>
                            <AccordionProvider>
                                <FAQEditorProvider>
                                    <div className={`h-screen w-screen overflow-hidden z-0 app-${app?.toLowerCase()} flex flex-row`}>
                                        {showQuickAppsMenu ? <NavigationAppsQuickAcessMenu /> : null}

                                        <div className='flex-1 min-w-0 h-full overflow-hidden'>
                                            <TopBar app={app} />

                                            <div className='flex flex-row flex-nowrap w-full h-full relative'>
                                                <motion.div
                                                    transition={{ duration: 0.2 }}
                                                    initial={{ x: 0 }}
                                                    animate={{
                                                        x: width <= 460 ? (isSidebarOpen ? 0 : -68) : 0,
                                                    }}
                                                    className='flex flex-row relative z-[0] max-[460px]:absolute left-0 top-0 max-[460px]:z-10 max-[460px]:h-full empreender-lib-sidebar '
                                                >
                                                    {/* Sidebar */}
                                                    <SidebarMenu>
                                                        <div className='overflow-x-hidden overflow-y-scroll custom-scroll zero-scroll rounded-md'>
                                                            {RoleSwitcherContent}
                                                            <div className='flex flex-col select-none [&_svg]:stroke-[2.3] [&_svg]:!size-4'>{sidebarElements}</div>
                                                        </div>

                                                        <div className='flex flex-col gap-2 select-none bg-white'>
                                                            {/* <ButtonSuporte app={app} /> */}
                                                            {!hideDash ? <ButtonDash /> : null}
                                                            {IntegracaoBtnChildren}

                                                            <div className='group/container relative'>
                                                                <PerfilWrapper>{children}</PerfilWrapper>

                                                                <AnimatePresence mode='wait'>
                                                                    {isSidebarOpen ? (
                                                                        <motion.div className='whitespace-nowrap text-[10px] w-full text-center text-gray-500 mt-2' key={'texto'} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.1 }} exit={{ opacity: 0 }}>
                                                                            <DesenvolvidoPor />
                                                                        </motion.div>
                                                                    ) : (
                                                                        <div className='w-full h-[15px] mt-2 bg-transparent' key={'linha'} />
                                                                    )}
                                                                </AnimatePresence>

                                                                <CopyButton onKeyCopy={onKeyCopy} />
                                                            </div>
                                                        </div>
                                                    </SidebarMenu>

                                                    <ThirdMenu>{contents}</ThirdMenu>
                                                </motion.div>

                                                {/* Aplicativo */}
                                                <SideBarContentComponent>{contentApp}</SideBarContentComponent>
                                                <Chat />
                                            </div>
                                        </div>
                                    </div>

                                    <CurrentStoreWrapper expiredAccountPopUpEnabled={expiredAccountPopUpEnabled} appEndPoint={appEndPoint} headers={headers} />
                                    <TourAuto />
                                </FAQEditorProvider>
                            </AccordionProvider>
                        </ChatProvider>
                    </SubMenuProvider>
                    <Toaster richColors />
                    <NavigationBlockerAuto navigate={navigate} />
                </TooltipProvider>
            </TranslationProvider>
        </UserProvider>
    );
};

const CopyButton = ({ onKeyCopy }: { onKeyCopy: (key: string) => void }) => {
    const { state, isSidebarOpen } = useSubMenu();
    const [, copy] = useCopyToClipboard();
    const { chave_empreender, name } = useUser()?.user || {};

    const copyKey = (e: React.MouseEvent<HTMLSpanElement>) => {
        e.stopPropagation();
        e.preventDefault();

        if (chave_empreender) copy(chave_empreender);

        if (onKeyCopy && chave_empreender) {
            onKeyCopy(chave_empreender);
        }
    };

    return (
        <>
            {isSidebarOpen && name ? (
                <span className='absolute top-[38px] left-[112px] block text-gray-500 cursor-pointer transition-all ease-in-out size-3 invisible opacity-100 group-hover/container:!opacity-100 group-hover/container:!visible' style={{ left: `${(state.chaveWidth ?? 0) + 5}px` }} onClick={copyKey}>
                    {chave_empreender ? <Copy className='size-full' /> : null}
                </span>
            ) : null}
        </>
    );
};

export default SideBar;
